// (Admin) Edit practice personas and push them to Retell ("Sync to Retell" creates/updates one agent each).
import { useState } from 'preact/hooks';
import type { AiStatus, PersonaRow, PersonaSyncResponse, PersonaUpdateResponse } from '../../../src/shared/api-ai';
import { api, errorMessage } from '../api';
import { Empty, ErrorBanner, toast } from '../components/ui';
import { KIND_LABEL } from './labels';

function PersonaEditor(props: { p: PersonaRow; onSaved: (p: PersonaRow) => void }) {
  const [name, setName] = useState(props.p.name);
  const [brief, setBrief] = useState(props.p.brief);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'warn' | 'bad'; text: string } | null>(null);
  const dirty = name !== props.p.name || brief !== props.p.brief;

  const save = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await api.put<PersonaUpdateResponse>(`/api/training/personas/${props.p.id}`, { name, brief });
      props.onSaved(r.persona);
      setMsg(r.syncError ? { kind: 'warn', text: r.syncError } : { kind: 'ok', text: r.synced ? 'Saved and pushed to Retell.' : 'Saved.' });
    } catch (e) {
      setMsg({ kind: 'bad', text: errorMessage(e) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div class="card stack" style="gap:6px">
      <div class="row" style="justify-content:space-between">
        <span class="chip">{KIND_LABEL[props.p.kind]}</span>
        {props.p.ready ? <span class="chip chip-ok">On Retell</span> : <span class="chip chip-warn">Not synced</span>}
      </div>
      <label>Name<input value={name} maxLength={80} style="width:100%" onInput={(e) => setName((e.currentTarget as HTMLInputElement).value)} /></label>
      <label>
        Brief (who they are, how they talk, what they object to, what would make them book; write "she"/"he" so the voice matches)
        <textarea rows={9} value={brief} maxLength={6000} style="width:100%;font-size:13px" onInput={(e) => setBrief((e.currentTarget as HTMLTextAreaElement).value)} />
      </label>
      <div class="row">
        <button class="btn btn-ink btn-sm" disabled={!dirty || busy} onClick={save}>{busy ? 'Saving…' : 'Save'}</button>
        {msg ? <span class={`chip chip-${msg.kind}`}>{msg.text}</span> : null}
      </div>
    </div>
  );
}

export function PersonaAdmin(props: { personas: PersonaRow[]; status: AiStatus | null; onChange: (personas: PersonaRow[]) => void }) {
  const [syncing, setSyncing] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<PersonaSyncResponse | null>(null);

  if (props.personas.length === 0) return <Empty>No personas yet. They're copied from the campaign template when a campaign is created.</Empty>;
  const campaigns = [...new Map(props.personas.map((p) => [p.campaignId, p])).values()];

  const sync = async (campaignId: number) => {
    setSyncing(campaignId); setError(null); setReport(null);
    try {
      const r = await api.post<PersonaSyncResponse>('/api/training/personas/sync', { campaignId });
      setReport(r);
      props.onChange([...props.personas.filter((p) => p.campaignId !== campaignId), ...r.personas]);
      const failed = r.results.filter((x) => x.action === 'failed').length;
      toast(failed ? `${failed} persona${failed === 1 ? '' : 's'} failed to sync.` : 'Personas synced to Retell.', failed ? 'bad' : 'ok');
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setSyncing(null);
    }
  };

  const replace = (p: PersonaRow) => props.onChange(props.personas.map((x) => (x.id === p.id ? p : x)));

  return (
    <div class="stack" style="gap:16px">
      {!props.status?.practice ? <div class="chip chip-warn" style="display:block;padding:8px 10px">Retell is not configured, so personas can be edited but not synced.</div> : null}
      <ErrorBanner error={error} />
      {report ? (
        <div class="card stack" style="gap:4px">
          <div class="eyebrow">Last sync</div>
          {report.results.map((r) => (
            <div style="font-size:13px">
              {KIND_LABEL[r.kind]}: {r.action === 'failed' ? <span class="chip chip-bad">{r.error}</span> : <>{r.action}, voice <span class="mono">{r.voiceId}</span>{r.published ? ', published' : ''}</>}
            </div>
          ))}
          {report.warnings.map((w) => <div class="chip chip-warn" style="display:block;padding:4px 8px">{w}</div>)}
        </div>
      ) : null}
      {campaigns.map((cp) => (
        <div class="stack" style="gap:8px">
          <div class="row" style="gap:8px">
            <h3 style="margin:0">{cp.campaignName}</h3>
            <span class="chip">{cp.campaignStatus}</span>
            <button class="btn btn-sm" disabled={!props.status?.practice || syncing !== null} onClick={() => sync(cp.campaignId)}
              title="Creates or updates one Retell agent per persona. No calls are placed and nothing is charged until someone practises.">
              {syncing === cp.campaignId ? 'Syncing…' : 'Sync to Retell'}
            </button>
          </div>
          <div class="tr-grid">
            {props.personas.filter((p) => p.campaignId === cp.campaignId).sort((a, b) => a.id - b.id).map((p) => <PersonaEditor key={p.id} p={p} onSaved={replace} />)}
          </div>
        </div>
      ))}
    </div>
  );
}
